import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode); // 'login', 'register', 'forgot', 'reset'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetToken] = useState(searchParams.get('token') || '');
  const navigate = useNavigate();
  const { loginWithEmail, register, loginWithGoogle, checkAuth } = useAuth();

  // If we have a reset token in URL, go to reset mode
  useEffect(() => {
    if (resetToken) setMode('reset');
  }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const res = await fetch(`${API}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to send reset email');
        setResetSent(true);
      } else if (mode === 'reset') {
        const res = await fetch(`${API}/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token: resetToken, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to reset password');
        await checkAuth();
        navigate('/dashboard', { replace: true });
      } else if (mode === 'register') {
        await register(name, email, password);
        // New tenant is unpaid — send them straight to the plan picker (also
        // the paywall destination). The picker shows a "welcome" header when
        // `from=register` is present.
        navigate('/billing/plans?from=register', { replace: true });
      } else {
        await loginWithEmail(email, password);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setResetSent(false);
  };

  return (
    <div className="min-h-screen bg-[#F7FAFB] flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0D1830] relative overflow-hidden flex-col justify-between p-12">
        <div className="relative z-10">
          <a href="https://nivela.ai" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors mb-12">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </a>
          <img src="/nivela-logo.png" alt="Nivela" className="h-10 brightness-0 invert mb-8" />
          <h1 className="text-3xl font-bold text-white leading-tight">
            AI-Powered<br />Construction Takeoffs
          </h1>
          <p className="mt-4 text-base text-[#72A4BF] leading-relaxed max-w-md">
            Upload tender documents. Get division-wise, assembly-wise takeoffs with cost estimates in minutes.
          </p>
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
            <div className="w-10 h-10 rounded-full bg-[#1D4053] flex items-center justify-center text-white font-bold text-sm shrink-0">N</div>
            <div>
              <p className="text-white text-sm font-medium">"Saves us hours on every bid."</p>
              <p className="text-[#72A4BF] text-xs mt-0.5">Construction Estimator</p>
            </div>
          </div>
        </div>
        {/* Decorative */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#1D4053]/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#72A4BF]/10 rounded-full blur-[80px]" />
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/">
              <img src="/nivela-logo.png" alt="Nivela" className="h-8 mx-auto" />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-[#0D1830]" data-testid="auth-title">
              {mode === 'login' && 'Welcome back'}
              {mode === 'register' && 'Create your account'}
              {mode === 'forgot' && 'Reset your password'}
              {mode === 'reset' && 'Set new password'}
            </h2>
            <p className="text-sm text-[#446E87] mt-1">
              {mode === 'login' && 'Sign in to continue to Nivela'}
              {mode === 'register' && 'Start automating your takeoffs'}
              {mode === 'forgot' && "Enter your email and we'll send a reset link"}
              {mode === 'reset' && 'Choose a new password for your account'}
            </p>
          </div>

          {/* Google Sign-In — only on login/register */}
          {(mode === 'login' || mode === 'register') && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={loginWithGoogle}
                data-testid="google-signin-btn"
                className="w-full h-11 rounded-xl border-[#E2E8ED] hover:bg-[#F7FAFB] hover:border-[#72A4BF]/40 transition-all text-sm font-medium text-[#0D1830] mb-6"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E8ED]" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#F7FAFB] px-3 text-[#72A4BF]">or continue with email</span>
                </div>
              </div>
            </>
          )}

          {/* Forgot Password - success state */}
          {mode === 'forgot' && resetSent && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#0D1830] mb-2">Check your email</h3>
              <p className="text-sm text-[#446E87] mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <Button
                variant="outline"
                onClick={() => switchMode('login')}
                className="rounded-xl"
                data-testid="back-to-login-btn"
              >
                Back to Sign In
              </Button>
            </div>
          )}

          {/* Forms */}
          {!(mode === 'forgot' && resetSent) && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-xs font-medium text-[#446E87] mb-1.5 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#72A4BF]" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="register-name-input"
                      className="pl-10 h-11 rounded-xl border-[#E2E8ED] focus:border-[#1D4053] focus:ring-[#1D4053]/10"
                      required
                    />
                  </div>
                </div>
              )}

              {mode !== 'reset' && (
                <div>
                  <Label htmlFor="email" className="text-xs font-medium text-[#446E87] mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#72A4BF]" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="auth-email-input"
                      className="pl-10 h-11 rounded-xl border-[#E2E8ED] focus:border-[#1D4053] focus:ring-[#1D4053]/10"
                      required
                    />
                  </div>
                </div>
              )}

              {(mode === 'login' || mode === 'register' || mode === 'reset') && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="password" className="text-xs font-medium text-[#446E87]">
                      {mode === 'reset' ? 'New Password' : 'Password'}
                    </Label>
                    {mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => switchMode('forgot')}
                        data-testid="forgot-password-link"
                        className="text-xs text-[#1D4053] hover:underline font-medium"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#72A4BF]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={mode === 'reset' ? 'Min. 6 characters' : mode === 'register' ? 'Min. 6 characters' : 'Enter your password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="auth-password-input"
                      className="pl-10 pr-10 h-11 rounded-xl border-[#E2E8ED] focus:border-[#1D4053] focus:ring-[#1D4053]/10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#72A4BF] hover:text-[#1D4053] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600" data-testid="auth-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full h-11 rounded-xl bg-[#0D1830] hover:bg-[#1D4053] text-white font-medium text-sm transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  { login: 'Sign In', register: 'Create Account', forgot: 'Send Reset Link', reset: 'Set New Password' }[mode]
                )}
              </Button>
            </form>
          )}

          {/* Footer links */}
          <div className="mt-6 text-center text-sm text-[#446E87] space-y-1">
            {mode === 'login' && (
              <p>
                Don't have an account?{' '}
                <button onClick={() => switchMode('register')} data-testid="switch-to-register" className="font-semibold text-[#1D4053] hover:underline">Sign up</button>
              </p>
            )}
            {mode === 'register' && (
              <p>
                Already have an account?{' '}
                <button onClick={() => switchMode('login')} data-testid="switch-to-login" className="font-semibold text-[#1D4053] hover:underline">Sign in</button>
              </p>
            )}
            {mode === 'forgot' && !resetSent && (
              <p>
                Remember your password?{' '}
                <button onClick={() => switchMode('login')} data-testid="back-to-login" className="font-semibold text-[#1D4053] hover:underline">Sign in</button>
              </p>
            )}
            {mode === 'reset' && (
              <p>
                <button onClick={() => switchMode('login')} className="font-semibold text-[#1D4053] hover:underline">Back to Sign In</button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
